import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DateRange } from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import React from "react";

import { PlanId } from "components/common/boundary/PlanId";
import { useTenantTranslation } from "hooks/formatters";
import { useShortLanguage, useTenant } from "hooks/settings";
import { backendApi } from "src/store/api/enhancedApi";
import {
  DeployParameters,
  PlannedResultsRead,
  SupportedLanguage,
  useGetPlanResultsQuery,
} from "src/store/api/generatedApi";
import { SafeDateTimeRangePicker } from "components/SafeDateTimeRangePicker";

interface Props {
  toNextState: () => void;
  toPreviousState: () => void;
  toInitialState: () => void;
  planId: PlanId;
}

interface BodyProps {
  toNextState: () => void;
  toPreviousState: () => void;
  toInitialState: () => void;
  planId: PlanId;
  startAt: Dayjs;
  endAt: Dayjs;
}

enum State {
  Review,
  Sending,
  Success,
  Error,
}

const getPlanStartAt = (planResults: PlannedResultsRead) => {
  if (planResults.start_at) {
    return dayjs(new Date(planResults.start_at)).subtract(2, "hours");
  } else {
    return dayjs(new Date());
  }
};

const getPlanEndAt = (planResults: PlannedResultsRead, startAt: Dayjs) => {
  if (planResults.end_at) {
    return dayjs(new Date(planResults.end_at));
  } else {
    return startAt.add(6, "hours");
  }
};

export const SendToScrapyard = ({
  toNextState,
  toPreviousState,
  toInitialState,
  planId,
}: Props) => {
  const tenantName = useTenant();
  const planResults = useGetPlanResultsQuery({
    tenantName,
    planId,
    period: 1,
  });
  if (planResults.data) {
    const startAt = getPlanStartAt(planResults.data);
    const endAt = getPlanEndAt(planResults.data, startAt);
    return (
      <Body
        toNextState={toNextState}
        toPreviousState={toPreviousState}
        toInitialState={toInitialState}
        planId={planId}
        startAt={startAt}
        endAt={endAt}
      />
    );
  } else {
    return null;
  }
};

export const Body = ({
  toNextState,
  toPreviousState,
  toInitialState,
  planId,
  startAt,
  endAt,
}: BodyProps) => {
  const { t } = useTenantTranslation();
  const [state, setState] = React.useState(State.Review);
  const theme = useTheme();
  const tenantName = useTenant();
  const language = useShortLanguage() as SupportedLanguage;
  const [dateRange, setDateRange] = React.useState<DateRange<Dayjs>>([
    startAt,
    endAt,
  ]);
  const [startDate, endDate] = dateRange;

  const [deployPlan] = backendApi.endpoints.deployPlan.useMutation();

  const handleSendToScrapyard = async () => {
    if (startDate !== null && endDate !== null) {
      setState(State.Sending);
      try {
        const deployParameters: DeployParameters = {
          process: "edit_and_send",
          language: language,
          start_at: startDate.toISOString(),
          end_at: endDate.toISOString(),
          deployment_type: "standard",
        };
        await deployPlan({ tenantName, planId, deployParameters }).unwrap();
        setState(State.Success);
      } catch {
        setState(State.Error);
      }
    }
  };

  const handleDateRangeChange = (newDateRange: DateRange<Dayjs>) => {
    setDateRange(newDateRange);
  };

  switch (state) {
    case State.Review: {
      return (
        <Stack
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            margin: "0 auto",
            width: { xs: "100%", sm: "500px" },
            maxWidth: "500px",
            marginTop: { xs: 4, sm: 10 },
            px: { xs: 2, sm: 0 },
          }}
        >
          <Typography variant="h6">{t("whenPlanActiveInScrapyard")}</Typography>
          <SafeDateTimeRangePicker
            localeText={{ start: t("planStart"), end: t("planEnd") }}
            value={[startDate, endDate]}
            onChange={handleDateRangeChange}
            ampm={false}
          />
          <Box
            sx={{
              gap: 1,
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              sx={{ alignSelf: "start" }}
              onClick={toPreviousState}
            >
              {t("back")}
            </Button>
            <Button
              variant="contained"
              sx={{ alignSelf: "start" }}
              onClick={() => void handleSendToScrapyard()}
            >
              {t("sendToScrapyardButton")}
            </Button>
          </Box>
        </Stack>
      );
    }
    case State.Sending: {
      return (
        <Stack
          sx={{
            height: "100%",
            margin: "0 auto",
            alignItems: "center",
            gap: 2,
            justifyContent: "center",
            px: { xs: 2, sm: 0 },
            textAlign: "center",
          }}
        >
          <CircularProgress />
          <Typography variant="h5" fontWeight={theme.typography.fontWeightBold}>
            {t("sendingToScrapyard")}...
          </Typography>
          <Typography variant="body1">
            {t("pleaseDoNotCloseThisWindow")}
          </Typography>
        </Stack>
      );
    }
    case State.Success: {
      return (
        <Stack
          sx={{
            height: "100%",
            margin: "0 auto",
            alignItems: "center",
            gap: 2,
            justifyContent: "center",
            px: { xs: 2, sm: 0 },
            textAlign: "center",
          }}
        >
          <CheckCircleIcon color="success" />
          <Typography variant="h5" fontWeight={theme.typography.fontWeightBold}>
            {t("successfullyDeployedToScrapyard")}
          </Typography>
          <Button
            variant="contained"
            onClick={toNextState}
            endIcon={<ArrowForwardIcon />}
          >
            {t("next")}
          </Button>
        </Stack>
      );
    }
    case State.Error: {
      return (
        <Stack
          sx={{
            height: "100%",
            margin: "0 auto",
            alignItems: "center",
            gap: 2,
            justifyContent: "center",
            px: { xs: 2, sm: 0 },
            textAlign: "center",
          }}
        >
          <ErrorIcon color="error" fontSize="large" />
          <Typography variant="body1">{t("unknownError")}</Typography>
          <Button
            variant="outlined"
            onClick={toInitialState}
            startIcon={<ArrowBackIcon />}
          >
            {t("back")}
          </Button>
        </Stack>
      );
    }
  }
};
